import {
  RenderProofMetrics,
  RenderProofResponse,
} from './api/RenderProofResponse';

const METRIC_FIELDS: Array<keyof RenderProofMetrics> = [
  'htmlSizeBytes',
  'linksCount',
  'headingsCount',
  'structuredDataCount',
  'openGraphTagsCount',
];

/** Every field present, finite and countable — anything else formats to NaN. */
function areMetricsUsable(metrics: RenderProofMetrics | undefined): boolean {
  if (metrics === undefined) return false;

  return METRIC_FIELDS.every((field) => {
    const value = metrics[field];
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
  });
}

/**
 * Whether the proof screen can honestly claim a successful render.
 *
 * `pending` has nothing rendered yet and `error` could not be read, so both
 * would render a "your first page has been rendered" screen full of dashes.
 *
 * A non-2xx render is *not* filtered out here: product asks for that case to be
 * shown with its real status and a "Not cached" cache status, which the screen
 * does. See `isSuccessfulRenderStatus`.
 */
export function hasComparableProof(
  proof: RenderProofResponse | undefined,
): proof is RenderProofResponse {
  if (proof === undefined) return false;
  if (proof.status !== 'complete' && proof.status !== 'partial') return false;
  return areMetricsUsable(proof.rendered);
}

/**
 * Whether the screen has something true to say, comparison or not.
 *
 * `origin_error` carries no metrics — there is no render to measure — but it
 * carries the status code that explains why, which is the whole point of the
 * warning state. Gating on a comparison would send exactly the customer whose
 * homepage is broken back to a screen congratulating them.
 */
export function hasReportableProof(
  proof: RenderProofResponse | undefined,
): proof is RenderProofResponse {
  if (proof === undefined) return false;
  if (proof.status === 'origin_error') {
    return typeof proof.renderStatusCode === 'number';
  }
  return hasComparableProof(proof);
}
